// assets/bb-favorites.js
// Meus favoritos. A lista fica no localStorage, na ordem escolhida pelo cliente, e guarda só
// { handle, variantId }. A gaveta busca cada item pela Section Rendering API
// (sections/favorites-drawer-item.liquid): foto, preço e estoque vêm sempre atualizados do Liquid.
// Usa do Dawn (global.js / pubsub.js / constants.js): trapFocus, removeTrapFocus, fetchConfig,
// publish e PUB_SUB_EVENTS.
(() => {
  if (customElements.get('favorites-drawer')) return;

  const STORAGE_KEY = 'bb_favorites';
  const CHANGE_EVENT = 'favorites:change';
  const ITEM_SECTION_ID = 'favorites-drawer-item';
  const MAX_ITEMS = 50;
  const AUTO_SCROLL_EDGE = 48; // px perto da borda da lista em que o arraste rola a gaveta
  const AUTO_SCROLL_STEP = 12;
  const PIXEL_EVENT_ADDED = 'wishlist:added';
  const PIXEL_EVENT_REMOVED = 'wishlist:removed';

  // Web pixel (Configurações > Eventos do cliente): o pixel recebe `data` em event.customData.
  // Formato: { variant_id, product_id, product_handle, product_title, price (ex.: 259.8), currency }.
  // Sem variante ou moeda não publica (o pixel descarta eventos fora do formato).
  function publishFavoriteEvent(name, data) {
    if (!data.variant_id || !data.currency) return;
    window.Shopify?.analytics?.publish?.(name, data);
  }

  const favoritesStore = {
    getAll() {
      try {
        const items = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return Array.isArray(items) ? items.filter((item) => item && item.handle) : [];
      } catch (error) {
        return [];
      }
    },

    get(handle) {
      return this.getAll().find((item) => item.handle === handle) || null;
    },

    has(handle) {
      return this.get(handle) !== null;
    },

    /** Retorna true se o produto entrou na lista, false se saiu. Itens novos entram no topo. */
    toggle(handle, variantId) {
      const items = this.getAll();
      const isSaved = items.some((item) => item.handle === handle);

      this.save(
        isSaved
          ? items.filter((item) => item.handle !== handle)
          : [{ handle, variantId }, ...items].slice(0, MAX_ITEMS)
      );
      return !isSaved;
    },

    remove(handle) {
      this.save(this.getAll().filter((item) => item.handle !== handle));
    },

    // Nova ordem vinda da gaveta. Itens que não estão em `handles` (ex.: produto que não carregou)
    // vão para o fim, na ordem em que estavam.
    reorder(handles, source) {
      const items = this.getAll();
      const byHandle = new Map(items.map((item) => [item.handle, item]));
      const ordered = handles.map((handle) => byHandle.get(handle)).filter(Boolean);
      const rest = items.filter((item) => !handles.includes(item.handle));
      this.save([...ordered, ...rest], source);
    },

    save(items, source) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (error) {
        return; // navegação privada ou cota cheia: não há onde salvar
      }
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { source } }));
    },
  };

  // A lista mudou em outra aba: atualiza esta também.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: {} }));
  });

  // Quantidade no coração do cabeçalho (sections/header.liquid): bolinha + aria-label do botão.
  // Busca os elementos a cada atualização porque o editor pode recarregar a seção do header.
  function updateHeaderCount() {
    const count = favoritesStore.getAll().length;

    document.querySelectorAll('[data-favorites-open]').forEach((button) => {
      const bubble = button.querySelector('[data-favorites-count]');
      if (bubble) {
        bubble.hidden = count === 0;
        bubble.querySelector('[data-favorites-count-number]').textContent = count;
      }

      const label = count > 0 ? button.dataset.labelCount?.replace('[quantity]', count) : button.dataset.label;
      if (label) button.setAttribute('aria-label', label);
    });
  }

  document.addEventListener(CHANGE_EVENT, updateHeaderCount);
  document.addEventListener('shopify:section:load', updateHeaderCount);
  updateHeaderCount();

  /** Botão "Adicionar aos favoritos" da página de produto (snippets/favorite-button.liquid). */
  class FavoriteButton extends HTMLElement {
    constructor() {
      super();
      this.label = this.querySelector('[data-favorite-label]');
      this.render = this.render.bind(this);

      this.querySelector('button').addEventListener('click', () => {
        // Removendo: vale a variante que foi salva, não a selecionada agora no formulário.
        const saved = favoritesStore.get(this.dataset.handle);
        const variantId = saved?.variantId || this.getCurrentVariantId();
        const wasAdded = favoritesStore.toggle(this.dataset.handle, variantId);
        publishFavoriteEvent(wasAdded ? PIXEL_EVENT_ADDED : PIXEL_EVENT_REMOVED, this.getEventData(variantId));
      });
    }

    connectedCallback() {
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    // A variante escolhida fica no input "id" do formulário de compra (o seletor de variantes e o
    // bundle trocam esse valor). O data-variant-id é só o valor inicial, para quando não há form.
    getCurrentVariantId() {
      const input = document.getElementById(this.dataset.formId)?.querySelector('[name="id"]');
      return input?.value || this.dataset.variantId;
    }

    render() {
      const isSaved = favoritesStore.has(this.dataset.handle);
      this.classList.toggle('is-saved', isSaved);
      this.label.textContent = isSaved ? this.dataset.labelRemove : this.dataset.labelAdd;
    }

    getEventData(variantId) {
      if (!this.prices) {
        try {
          this.prices = JSON.parse(this.querySelector('[data-favorite-prices]').textContent);
        } catch (error) {
          this.prices = {};
        }
      }

      return {
        variant_id: Number(variantId),
        product_id: Number(this.dataset.productId),
        product_handle: this.dataset.handle,
        product_title: this.dataset.productTitle,
        price: this.prices[variantId] ?? 0,
        currency: this.dataset.currency,
      };
    }
  }

  /** Gaveta "Meus favoritos" (sections/favorites-drawer.liquid). Abre só pelo coração do cabeçalho. */
  class FavoritesDrawer extends HTMLElement {
    constructor() {
      super();
      this.panel = this.querySelector('[data-favorites-panel]');
      this.closeButton = this.querySelector('[data-favorites-close-button]');
      this.body = this.querySelector('[data-favorites-body]');
      this.list = this.querySelector('[data-favorites-list]');
      this.hint = this.querySelector('[data-favorites-hint]');
      this.status = this.querySelector('[data-favorites-status]');
      this.emptyState = this.querySelector('[data-favorites-empty]');
      this.loadingState = this.querySelector('[data-favorites-loading]');
      this.itemRequests = new Map();
      this.renderCount = 0;
      this.drag = null;
      this.renderAfterDrag = false; // mudança vinda de outra aba durante um arraste

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onFavoritesChange = this.onFavoritesChange.bind(this);
      this.onDragMove = this.onDragMove.bind(this);
      this.onDragEnd = this.onDragEnd.bind(this);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
      this.list.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.list.addEventListener('keydown', this.onListKeydown.bind(this));
    }

    connectedCallback() {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener(CHANGE_EVENT, this.onFavoritesChange);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener(CHANGE_EVENT, this.onFavoritesChange);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    // Delegado no document: o coração continua funcionando quando o editor recarrega o header.
    onDocumentClick(event) {
      const trigger = event.target.closest('[data-favorites-open]');
      if (!trigger) return;

      event.preventDefault();
      this.open(trigger);
    }

    onFavoritesChange(event) {
      if (!this.isOpen || event.detail?.source === 'drawer') return; // a reordenação já está na tela
      if (this.drag) {
        this.renderAfterDrag = true;
        return;
      }
      this.render();
    }

    onClick(event) {
      if (event.target.closest('[data-favorites-close]')) {
        this.close();
        return;
      }

      const addButton = event.target.closest('[data-favorites-add]');
      if (addButton) {
        this.addToCart(addButton);
        return;
      }

      const removeButton = event.target.closest('[data-favorites-remove]');
      if (removeButton) {
        const { dataset } = removeButton.closest('[data-favorites-item]');
        publishFavoriteEvent(PIXEL_EVENT_REMOVED, {
          variant_id: Number(dataset.variantId),
          product_id: Number(dataset.productId),
          product_handle: dataset.handle,
          product_title: dataset.productTitle,
          price: Number(dataset.price) || 0,
          currency: dataset.currency,
        });
        favoritesStore.remove(dataset.handle);
      }
    }

    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      trapFocus(this.panel, this.closeButton);
      this.render();
    }

    close() {
      if (this.drag) this.finishDrag();
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.trigger);
    }

    async render() {
      const renderId = ++this.renderCount;
      const favorites = favoritesStore.getAll();

      this.loadingState.hidden = favorites.length === 0 || this.list.childElementCount > 0;
      this.emptyState.hidden = true;

      const items = await Promise.all(favorites.map((favorite) => this.getItem(favorite)));
      if (renderId !== this.renderCount) return; // uma renderização mais nova já começou

      const elements = items.filter(Boolean);
      this.list.replaceChildren(...elements);
      this.list.classList.toggle('is-sortable', elements.length > 1);
      this.hint.hidden = elements.length < 2;
      this.loadingState.hidden = true;
      this.emptyState.hidden = elements.length > 0;
      if (this.isOpen) this.refreshFocusTrap();
    }

    // O trapFocus do Dawn guarda o primeiro e o último focável na hora da chamada: refaz depois de
    // mudar a lista. Se o elemento que tinha o foco sumiu (item removido), o foco volta para o X.
    refreshFocusTrap() {
      const focusTarget = this.panel.contains(document.activeElement) ? document.activeElement : this.closeButton;
      trapFocus(this.panel, focusTarget);
    }

    // Uma requisição por item durante a visita; se falhar, sai do cache e tenta de novo na próxima abertura.
    getItem({ handle, variantId }) {
      const key = `${handle}:${variantId}`;
      if (!this.itemRequests.has(key)) this.itemRequests.set(key, this.fetchItem(handle, variantId));

      return this.itemRequests.get(key).then((item) => {
        if (!item) this.itemRequests.delete(key);
        return item ? item.cloneNode(true) : null;
      });
    }

    async fetchItem(handle, variantId) {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({ section_id: ITEM_SECTION_ID });
      if (variantId) params.set('variant', variantId);

      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?${params}`);
        if (!response.ok) return null; // produto apagado ou despublicado

        const html = await response.text();
        return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-favorites-item]');
      } catch (error) {
        return null;
      }
    }

    // ---------- Reordenar: arrastar (mouse e toque, via Pointer Events) ----------

    onPointerDown(event) {
      const handle = event.target.closest('[data-favorites-handle]');
      if (!handle || this.drag || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (!this.list.classList.contains('is-sortable')) return;

      event.preventDefault(); // sem seleção de texto nem arraste nativo de imagem
      const item = handle.closest('[data-favorites-item]');
      try {
        handle.setPointerCapture(event.pointerId);
      } catch (error) {
        // sem captura (ponteiro já liberado): os ouvintes no window continuam recebendo o movimento
      }

      this.drag = {
        item,
        pointerId: event.pointerId,
        startY: event.clientY,
        startScroll: this.body.scrollTop,
        moved: false,
      };
      item.classList.add('is-dragging');
      this.classList.add('is-sorting');
      window.addEventListener('pointermove', this.onDragMove);
      window.addEventListener('pointerup', this.onDragEnd);
      window.addEventListener('pointercancel', this.onDragEnd);
    }

    onDragMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.autoScroll(event.clientY);
      this.followPointer(event.clientY);
    }

    // Troca o item de lugar quando o ponteiro passa do meio do vizinho (o while cobre movimentos
    // rápidos que cruzam vários itens) e desloca o item com translate para acompanhar o ponteiro.
    followPointer(clientY) {
      const { item } = this.drag;

      while (item.nextElementSibling && clientY > this.middleOf(item.nextElementSibling)) {
        this.moveItem(item, item.nextElementSibling, 'after');
      }
      while (item.previousElementSibling && clientY < this.middleOf(item.previousElementSibling)) {
        this.moveItem(item, item.previousElementSibling, 'before');
      }

      const scrolled = this.body.scrollTop - this.drag.startScroll;
      item.style.transform = `translateY(${clientY - this.drag.startY + scrolled}px)`;
    }

    // offsetTop ignora o translate: a diferença compensa o salto do item para o novo lugar.
    moveItem(item, sibling, position) {
      const topBefore = item.offsetTop;
      sibling[position](item);
      this.drag.startY += item.offsetTop - topBefore;
      this.drag.moved = true;
    }

    middleOf(element) {
      const rect = element.getBoundingClientRect();
      return rect.top + rect.height / 2;
    }

    autoScroll(clientY) {
      const rect = this.body.getBoundingClientRect();
      if (clientY < rect.top + AUTO_SCROLL_EDGE) this.body.scrollTop -= AUTO_SCROLL_STEP;
      else if (clientY > rect.bottom - AUTO_SCROLL_EDGE) this.body.scrollTop += AUTO_SCROLL_STEP;
    }

    onDragEnd(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.finishDrag();
    }

    finishDrag() {
      const { item, moved } = this.drag;
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.onDragEnd);
      window.removeEventListener('pointercancel', this.onDragEnd);
      item.style.transform = '';
      item.classList.remove('is-dragging');
      this.classList.remove('is-sorting');
      this.drag = null;

      if (moved) this.commitOrder(item);
      if (this.renderAfterDrag) {
        this.renderAfterDrag = false;
        this.render();
      }
    }

    // ---------- Reordenar: teclado (setas na alça) ----------

    onListKeydown(event) {
      const handle = event.target.closest('[data-favorites-handle]');
      if (!handle || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;

      event.preventDefault();
      const item = handle.closest('[data-favorites-item]');
      const sibling = event.key === 'ArrowUp' ? item.previousElementSibling : item.nextElementSibling;
      if (!sibling) return;

      sibling[event.key === 'ArrowUp' ? 'before' : 'after'](item);
      handle.focus(); // mover o nó no DOM tira o foco dele
      this.commitOrder(item);
    }

    // Salva a ordem da tela, anuncia a nova posição para leitor de tela e refaz o trap de foco
    // (o último focável da gaveta pode ter mudado), deixando o foco na alça do item movido.
    commitOrder(item) {
      const items = [...this.list.querySelectorAll('[data-favorites-item]')];
      favoritesStore.reorder(
        items.map((element) => element.dataset.handle),
        'drawer'
      );

      const title = item.querySelector('.bb-favorites-item__title')?.textContent.trim() || '';
      this.status.textContent = this.dataset.movedMessage
        .replace('[title]', title)
        .replace('[position]', items.indexOf(item) + 1)
        .replace('[total]', items.length);
      trapFocus(this.panel, item.querySelector('[data-favorites-handle]'));
    }

    // ---------- Comprar ----------

    // Mesmo caminho do product-form.js do Dawn: /cart/add com as seções do carrinho, e o
    // cart-drawer (ou cart-notification) pinta o resultado com renderContents.
    async addToCart(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;

      const errorMessage = button.closest('[data-favorites-item]').querySelector('[data-favorites-error]');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { id: Number(button.dataset.variantId), quantity: 1 };

      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      button.setAttribute('aria-disabled', 'true');
      errorMessage.hidden = true;

      try {
        const response = await fetch(window.routes.cart_add_url, { ...fetchConfig(), body: JSON.stringify(body) });
        const data = await response.json();

        if (data.status) {
          this.showError(errorMessage, data.description || data.message);
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'favorites-drawer', productVariantId: body.id, cartData: data });

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        this.close();
        cart.setActiveElement(this.trigger);
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      } catch (error) {
        this.showError(errorMessage, this.dataset.errorMessage);
      } finally {
        button.removeAttribute('aria-disabled');
      }
    }

    showError(element, message) {
      element.textContent = message;
      element.hidden = false;
    }
  }

  customElements.define('favorite-button', FavoriteButton);
  customElements.define('favorites-drawer', FavoritesDrawer);
})();
